/**
 * Module-level request deduplication utility
 * Prevents duplicate queries (especially from React Strict Mode double-mounts)
 */

const requestCache = new Map();

const getCacheKey = (funcName, params) => {
  if (Array.isArray(params)) {
    return `${funcName}_${params.sort().join(',')}`;
  }
  if (typeof params === 'string' || typeof params === 'number') {
    return `${funcName}_${params}`;
  }
  return `${funcName}_${JSON.stringify(params)}`;
};

/**
 * Deduplicates requests by caching in-flight promises
 * @param {string} cacheKey - Unique key for this request
 * @param {Function} requestFn - Function that returns a promise
 * @param {object} options - Optional: { keepAliveMs } to hold cache longer (e.g. for profile requests)
 * @returns {Promise} - The cached promise if available, or new promise
 */
export const dedupeRequest = async (cacheKey, requestFn, options = {}) => {
  const { keepAliveMs = 100 } = options;

  // If request already in progress, return the existing promise
  if (requestCache.has(cacheKey)) {
    return requestCache.get(cacheKey);
  }

  // Create new request promise
  const promise = requestFn()
    .finally(() => {
      setTimeout(() => {
        requestCache.delete(cacheKey);
      }, keepAliveMs);
    });

  requestCache.set(cacheKey, promise);
  return promise;
};

/**
 * Helper to create cache key from function name and parameters
 */
export { getCacheKey };

